import cloneDeep from 'lodash/cloneDeep';

import { Error } from 'lib/types/global';
import {
  DeliveryArea,
  BlueprintArea,
  DeliveryRadius,
} from 'lib/types/deliveryAreas';
import { camelCase, removeNull } from 'lib/utils/objectSerializer';

import {
  UPDATE_DELIVERY_RADIUS,
  DISCARD_DELIVERY_RADIUS,
  SET_SELECTED_AREA,
  UPDATE_SELECTED_AREA,
  DISCARD_SELECTED_AREA,
  API_READ_DELIVERY_AREAS_REQUEST,
  API_READ_DELIVERY_AREAS_SUCCESS,
  API_READ_DELIVERY_AREAS_FAILURE,
  API_READ_BLUEPRINT_AREAS_REQUEST,
  API_READ_BLUEPRINT_AREAS_SUCCESS,
  API_READ_BLUEPRINT_AREAS_FAILURE,
  API_READ_DELIVERY_RADIUS_REQUEST,
  API_READ_DELIVERY_RADIUS_SUCCESS,
  API_READ_DELIVERY_RADIUS_FAILURE,
  API_SAVE_DELIVERY_RADIUS_REQUEST,
  API_SAVE_DELIVERY_RADIUS_SUCCESS,
  API_SAVE_DELIVERY_RADIUS_FAILURE,
  API_CREATE_DELIVERY_AREA_REQUEST,
  API_CREATE_DELIVERY_AREA_SUCCESS,
  API_CREATE_DELIVERY_AREA_FAILURE,
  API_SAVE_DELIVERY_AREA_REQUEST,
  API_SAVE_DELIVERY_AREA_SUCCESS,
  API_SAVE_DELIVERY_AREA_FAILURE,
  API_DELETE_DELIVERY_AREA_REQUEST,
  API_DELETE_DELIVERY_AREA_SUCCESS,
  API_DELETE_DELIVERY_AREA_FAILURE,
} from './actionTypes';
import { DeliveryAreasListAction } from './actions';

export interface DeliveryAreasListState {
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  deliveryAreas?: DeliveryArea[];
  deliveryAreasV1?: DeliveryArea[];
  blueprintAreas?: BlueprintArea[];
  deliveryRadius?: Partial<DeliveryRadius>;
  pristineDeliveryRadius?: DeliveryRadius;
  error?: Error;
  selectedArea?: Partial<DeliveryArea>;
  pristineSelectedArea?: Partial<DeliveryArea>;
  // a temporary flag to discern if the restaurant should show a coming soon banner
  hasBeenMigrated?: boolean;
}

export const initialState: DeliveryAreasListState = {
  loading: false,
  saving: false,
  deleting: false,
};

export default function deliveryAreasReducer(
  state: DeliveryAreasListState = initialState,
  action: DeliveryAreasListAction,
): DeliveryAreasListState {
  switch (action.type) {
    case UPDATE_DELIVERY_RADIUS:
      return {
        ...state,
        deliveryRadius: { ...state.deliveryRadius, ...action.update },
      };

    case DISCARD_DELIVERY_RADIUS:
      return {
        ...state,
        deliveryRadius: cloneDeep(state.pristineDeliveryRadius),
      };

    case SET_SELECTED_AREA:
      return {
        ...state,
        selectedArea: action.selectedArea,
        pristineSelectedArea: action.selectedArea,
      };

    case UPDATE_SELECTED_AREA:
      return {
        ...state,
        selectedArea: { ...state.selectedArea, ...action.update },
      };

    case DISCARD_SELECTED_AREA: {
      // when discarding the area, remove the keys from state entirely
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedArea, pristineSelectedArea, ...restOfState } = state;

      return restOfState;
    }

    // TODO: Once completed feature, maybe refactor:
    // https://github.com/deliveroo/restaurant-hub-app/pull/1842#discussion_r401567563
    case API_READ_BLUEPRINT_AREAS_REQUEST:
    case API_READ_DELIVERY_RADIUS_REQUEST:
    case API_READ_DELIVERY_AREAS_REQUEST:
      return {
        ...state,
        loading: true,
        error: undefined,
        hasBeenMigrated: undefined,
        deliveryAreas: [],
        blueprintAreas: [],
        deliveryAreasV1: [],
      };

    case API_SAVE_DELIVERY_RADIUS_REQUEST:
    case API_SAVE_DELIVERY_AREA_REQUEST:
    case API_CREATE_DELIVERY_AREA_REQUEST:
      return { ...state, saving: true, error: undefined };
    case API_DELETE_DELIVERY_AREA_REQUEST:
      return { ...state, deleting: true, error: undefined };

    case API_READ_DELIVERY_AREAS_FAILURE:
    case API_READ_BLUEPRINT_AREAS_FAILURE:
    case API_READ_DELIVERY_RADIUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: camelCase(action.payload),
        hasBeenMigrated: false,
      };

    case API_SAVE_DELIVERY_RADIUS_FAILURE:
    case API_SAVE_DELIVERY_AREA_FAILURE:
    case API_CREATE_DELIVERY_AREA_FAILURE:
      return { ...state, saving: false, error: camelCase(action.payload) };
    case API_DELETE_DELIVERY_AREA_FAILURE: {
      return { ...state, deleting: false, error: camelCase(action.payload) };
    }

    case API_READ_DELIVERY_AREAS_SUCCESS: {
      const baseState = { ...state, loading: false };

      if (action.payload[0].geometry) {
        return {
          ...baseState,
          deliveryAreasV1: removeNull(camelCase(action.payload)),
        };
      }

      return {
        ...baseState,
        deliveryAreas: removeNull(camelCase(action.payload)),
      };
    }

    case API_READ_BLUEPRINT_AREAS_SUCCESS:
      return {
        ...state,
        loading: false,
        blueprintAreas: removeNull(camelCase(action.payload)),
      };

    case API_READ_DELIVERY_RADIUS_SUCCESS: {
      const deliveryRadius = removeNull(camelCase(action.payload));
      const pristineDeliveryRadius = cloneDeep(deliveryRadius);

      return {
        ...state,
        loading: false,
        deliveryRadius,
        pristineDeliveryRadius,
        hasBeenMigrated: !!action.payload.km,
      };
    }

    case API_SAVE_DELIVERY_RADIUS_SUCCESS: {
      const deliveryRadius = removeNull(camelCase(action.payload));
      const pristineDeliveryRadius = cloneDeep(deliveryRadius);
      return {
        ...state,
        saving: false,
        deliveryRadius,
        pristineDeliveryRadius,
      };
    }

    case API_CREATE_DELIVERY_AREA_SUCCESS: {
      // when creating an area, reset the selectedArea state
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedArea, pristineSelectedArea, ...restOfState } = state;

      const newDeliveryArea = removeNull(camelCase(action.payload));

      return {
        ...restOfState,
        saving: false,
        deliveryAreas: [...state.deliveryAreas!, newDeliveryArea],
      };
    }

    case API_SAVE_DELIVERY_AREA_SUCCESS: {
      // when saving an area, reset the selectedArea state
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedArea, pristineSelectedArea, ...restOfState } = state;

      const filterOutEditedArea = state.deliveryAreas?.filter((area) => {
        return area.id !== action.extra.areaId;
      });

      return {
        ...restOfState,
        deliveryAreas: [...filterOutEditedArea!, action.extra.deliveryArea],
        saving: false,
      };
    }

    case API_DELETE_DELIVERY_AREA_SUCCESS: {
      // when editing an area, reset the selectedArea state
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedArea, pristineSelectedArea, ...restOfState } = state;

      const filterOutDeletedArea = state.deliveryAreas?.filter((area) => {
        return area.id !== action.extra.areaId;
      });

      return {
        ...restOfState,
        deleting: false,
        deliveryAreas: filterOutDeletedArea,
      };
    }

    default:
      return state;
  }
}
